import '@/styles/tour.css';
import { AllowedButtons, driver, Driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AppTourStep,
  EmptyStatePopover,
  FlowVariant,
  getTourFlow,
  InteractiveStepConfig,
  MissingStatePopover,
  resolveFlowSteps,
  TourAccess,
  TourFlow,
  tourFlows,
} from '@/constants/tour-steps';
import { PermissionContext } from '@/contexts/PermissionContext/context';
import { resetTourDemoState, setTourMode, setTourStepTargets } from '@/tours';

import { TourContext } from './TourContextType';

// A wizard's step indicator is either MUI's Stepper (`.MuiStepper-root`) or the
// newer StepProgress pill (`[data-stepper-root]`). These selectors match both so
// stepper-wizard tours can wait for and click stages regardless of which one a
// page renders.
const STEPPER_ROOT_SELECTOR = '.MuiStepper-root, [data-stepper-root]';
const STEPPER_LABEL_SELECTOR =
  '.MuiStepper-root .MuiStepLabel-root, [data-stepper-root] [data-stepper-step]';

const LEGACY_STORAGE_KEY = 'app_tour_completed';

// How long startTour waits for its navigation to commit before giving up.
const ROUTE_ARRIVAL_TIMEOUT_MS = 10000;

const TOUR_STORAGE_PREFIX = 'app_tour_completed::';
const TOUR_PROGRESS_PREFIX = 'app_tour_progress::';
const TOUR_VARIANT_PREFIX = 'app_tour_variant::';
const storageKeyFor = (flowId: string) => `${TOUR_STORAGE_PREFIX}${flowId}`;
const progressKeyFor = (flowId: string) => `${TOUR_PROGRESS_PREFIX}${flowId}`;
// Which step list (main / fallback) an interrupted run was on, so Resume
// reopens the same list the saved step index belongs to.
const variantKeyFor = (flowId: string) => `${TOUR_VARIANT_PREFIX}${flowId}`;

const readVariant = (flowId: string): FlowVariant | null => {
  const raw = localStorage.getItem(variantKeyFor(flowId));
  return raw === 'main' || raw === 'fallback' ? raw : null;
};

const readProgress = (flowId: string): number => {
  const raw = localStorage.getItem(progressKeyFor(flowId));
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

export { TourContext, type TourContextType } from './TourContextType';

type ListenerCleanup = () => void;

/**
 * Wait for a DOM element matching `selector` to appear AND be ready
 * (visible, non-zero size). Resolves with the element or null on timeout.
 * Used to gate the tour until the page has finished loading so the
 * spotlight doesn't land on an empty loader.
 */
const waitForElement = (
  selector: string,
  timeoutMs = 5000
): Promise<HTMLElement | null> => {
  return new Promise(resolve => {
    const isReady = (el: HTMLElement | null) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    const existing = document.querySelector<HTMLElement>(selector);
    if (isReady(existing)) {
      resolve(existing);
      return;
    }

    let resolved = false;
    const finish = (el: HTMLElement | null) => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    };

    const observer = new MutationObserver(() => {
      const el = document.querySelector<HTMLElement>(selector);
      if (isReady(el)) finish(el);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    const timer = setTimeout(() => {
      finish(document.querySelector<HTMLElement>(selector));
    }, timeoutMs);
  });
};

/**
 * Re-anchor the popover after an `advanceClick` if the advance swapped a *keyed*
 * React subtree out from under driver.js.
 *
 * The action behind an `advanceClick` (e.g. clicking a dashboard card) can
 * unmount the next step's target node and mount a fresh one — the dashboard
 * section is keyed by card code, so switching cards replaces the whole subtree.
 * That remount commits on a LATER tick than our synchronous `moveNext()`, so
 * driver highlights the old node, which then detaches and strands the popover in
 * the top-left corner. `refresh()` can't recover (it repositions the stored,
 * detached node) — only re-driving the step re-resolves the selector (`j()` in
 * driver.js re-queries string selectors on highlight).
 *
 * Rather than guess how long the remount takes (too short re-breaks it, too long
 * lags the no-remount case), we advance immediately — correct already when no
 * remount happens — and watch for the swap. The instant the captured node
 * detaches and a different, laid-out node matches the selector, we re-drive the
 * step (`moveTo`) so driver picks up the live node. If no remount lands, nothing
 * happens. `timeoutMs` is only a safety cap on how long we watch.
 *
 * The interactive-click path doesn't need this: it awaits `waitFor` before
 * advancing, by which point the new content already exists.
 */
const reanchorOnRemount = (
  driverRef: React.RefObject<Driver | null>,
  selector: string,
  staleNode: Element | null,
  stepIndex: number,
  timeoutMs = 3000
): (() => void) => {
  if (!staleNode) return () => {};
  let done = false;
  let raf = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const stop = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
  };
  const laidOut = (el: Element | null): boolean => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };
  const poll = () => {
    if (done) return;
    // The remount has landed once the captured node is gone AND a different,
    // laid-out node now matches the selector.
    if (!document.body.contains(staleNode)) {
      const el = document.querySelector(selector);
      if (el && el !== staleNode && laidOut(el)) {
        driverRef.current?.moveTo(stepIndex);
        stop();
        return;
      }
    }
    raf = requestAnimationFrame(poll);
  };
  timer = setTimeout(stop, timeoutMs);
  raf = requestAnimationFrame(poll);
  return stop;
};

/**
 * Resolve once `el`'s on-screen box stops moving (unchanged for two consecutive
 * frames), or after `timeoutMs`. `waitFor` resolves as soon as a drawer mounts,
 * but MUI's slide-in is still running then; driver.js places the popover once,
 * mid-animation, against that moving box — so a `side: "left"` popover lands on
 * top of the settled drawer. Waiting for the slide to finish first keeps it
 * clear of the panel.
 */
const waitForLayoutSettle = (
  el: Element | null,
  timeoutMs = 600
): Promise<void> => {
  if (!el) return Promise.resolve();
  return new Promise(resolve => {
    const deadline = Date.now() + timeoutMs;
    let last = el.getBoundingClientRect();
    let stableFrames = 0;
    const tick = () => {
      const rect = el.getBoundingClientRect();
      const moved =
        rect.left !== last.left ||
        rect.top !== last.top ||
        rect.width !== last.width ||
        rect.height !== last.height;
      stableFrames = moved ? 0 : stableFrames + 1;
      last = rect;
      if (stableFrames >= 2 || Date.now() >= deadline) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
};

/**
 * Destroy a driver without its focus restore. driver.js records
 * `document.activeElement` every time a step is shown and calls `.focus()` on it
 * when destroyed. If the user had just picked a filter value, that is the
 * Autocomplete input — and the filter panels use `openOnFocus`, so ending the
 * tour (Finish / close) popped the dropdown open on its own.
 */
const destroyWithoutRefocus = (obj: Driver) => {
  const state = obj.getState();
  if (state) state.__activeOnDestroyed = undefined;
  obj.destroy();
};

const readCompleted = (flowId: string): boolean =>
  localStorage.getItem(storageKeyFor(flowId)) === 'true';

/**
 * Match a step's declared route against the live pathname. A segment that
 * begins with ':' (e.g. "/tenant/final-award/:awardNo") is treated as a
 * wildcard, so tours can drive dynamic detail pages whose URL carries a
 * record id we can't know ahead of time.
 */
const routeMatches = (pattern: string, pathname: string): boolean => {
  if (pattern === pathname) return true;
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every(
    (part, i) => part.startsWith(':') || part === pathParts[i]
  );
};

/**
 * Swap a popover to its empty-state copy when the step's target is currently
 * rendering an empty state (e.g. a data table with no rows).
 */
const applyEmptyStateCopy = (
  popover: any,
  emptyState: EmptyStatePopover | undefined
) => {
  if (!emptyState) return;
  const emptySelector =
    emptyState.selector || '[data-tour="table-empty-state"]';
  if (!document.querySelector(emptySelector)) return;
  if (emptyState.title && popover.title) {
    popover.title.innerText = emptyState.title;
  }
  if (popover.description) {
    popover.description.innerText = emptyState.description;
  }
};

/**
 * Wait for a page's entry step to render: the tour starts before API loaders
 * finish, and highlighting too early leaves a dark overlay on an empty page.
 * Leading skippable steps may never render, so wait for whichever of them — or
 * the first step that cannot be skipped — shows up first.
 */
const waitForPageEntry = async (
  pageSteps: { element?: DriveStep['element']; __skipIfMissing?: string }[]
): Promise<HTMLElement | null> => {
  const candidates: string[] = [];
  for (const step of pageSteps) {
    if (typeof step.element === 'string') candidates.push(step.element);
    if (!step.__skipIfMissing) break;
  }
  return candidates.length ? waitForElement(candidates.join(', '), 6000) : null;
};

/**
 * Swap a popover to its alternate copy when the step's `whenMissing` selector
 * is absent (e.g. no Accept button on an award that no longer takes a
 * response). The inverse of applyEmptyStateCopy.
 */
const applyMissingStateCopy = (
  popover: any,
  whenMissing: MissingStatePopover | undefined
) => {
  if (!whenMissing || document.querySelector(whenMissing.selector)) return;
  if (whenMissing.title && popover.title) {
    popover.title.innerText = whenMissing.title;
  }
  if (popover.description) {
    popover.description.innerText = whenMissing.description;
  }
};

/**
 * True when an `appear` step's `waitFor` target is already on screen.
 *
 * The `appear` trigger watches for something to SHOW UP as a result of the
 * user's action (e.g. the award table after a search). If it is already there
 * when the step is armed — the user clicked Back onto the step, or ran the
 * action before the tour reached it — there is no transition left to observe.
 * Arming anyway means the MutationObserver fires on the next unrelated DOM
 * change (driver.js rendering its own popover is enough) and bounces the tour
 * straight forward again, so Back looks like it does nothing.
 */
const isAppearAlreadySatisfied = (
  interactive: InteractiveStepConfig | undefined
): boolean => {
  if (interactive?.trigger !== 'appear' || !interactive.waitFor) return false;
  const node = document.querySelector(interactive.waitFor);
  if (!node) return false;
  const rect = node.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** The text box a step points at: the target itself, or the first one inside. */
const findStepInput = (target: HTMLElement): HTMLElement =>
  (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
    ? target
    : target.querySelector<HTMLInputElement>('input, textarea')) || target;

/**
 * Lock the step's input to `expected` while `isActive()` holds: a keystroke
 * that would leave the box as anything but a case-insensitive prefix of it is
 * refused. Text already in the box that is not a prefix can still be deleted.
 *
 * The listener sits on the input itself, so it runs before React's root-level
 * listener. Assigning `value` on the element also updates React's value
 * tracker, so React sees no change and its onChange never fires for the
 * refused keystroke.
 */
const restrictInputToValue = (
  target: HTMLElement,
  expected: string,
  isActive: () => boolean
): (() => void) => {
  const inputEl = findStepInput(target) as HTMLInputElement;
  const wanted = expected.toLowerCase();
  const isPrefix = (value: string) => wanted.startsWith(value.toLowerCase());
  let lastValue = inputEl.value ?? '';

  const handler = () => {
    const next = inputEl.value ?? '';
    const allowed =
      !isActive() ||
      isPrefix(next) ||
      (!isPrefix(lastValue) && next.length < lastValue.length);
    if (allowed) {
      lastValue = next;
      return;
    }
    inputEl.value = lastValue;
  };
  inputEl.addEventListener('input', handler);
  return () => inputEl.removeEventListener('input', handler);
};

/** A route that carries a dynamic `:param` segment cannot be navigated to. */
const isDynamicRoute = (route: string): boolean =>
  route.split('/').some(part => part.startsWith(':'));

/**
 * The step a (re)started flow opens on. The saved index only counts when the
 * run's step list is known and still has that step. A saved step on a dynamic
 * detail route (e.g. /tenant/final-award/:awardNo) has no id to navigate back
 * to, so the flow restarts from the top — it re-enters the detail page through
 * its parent list page. (Single-route stepper wizards keep their saved step;
 * driveCurrentPage restores the wizard stage before highlighting it — see the
 * `stepperWizard` flag.)
 */
const resolveResumeIdx = (
  steps: AppTourStep[],
  savedIdx: number,
  variant: FlowVariant | null
): number => {
  const idx = variant && savedIdx < steps.length ? savedIdx : 0;
  return isDynamicRoute(steps[idx].route) ? 0 : idx;
};

/** A flow step as handed to driver.js, with the tour's own options riding along. */
type PreparedStep = DriveStep & {
  __interactive?: InteractiveStepConfig;
  __skipIfMissing?: string;
  __blockInteraction?: boolean;
  __emptyState?: EmptyStatePopover;
  __whenMissing?: MissingStatePopover;
  __advanceClick?: string;
  __undoClick?: string;
  __allowClick?: string;
};

/** Split a flow step into the driver.js step and the tour's own options. */
const prepareStep = (step: AppTourStep): PreparedStep => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    route: _,
    interactive,
    skipIfMissing,
    blockInteraction,
    emptyState,
    whenMissing,
    advanceClick,
    undoClick,
    allowClick,
    // Permission gates were applied when the steps were resolved.
    // eslint-disable-next-line @typescript-eslint/naming-convention
    accessKey: _accessKey,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    accessPattern: _accessPattern,
    ...driverStep
  } = step;
  const prepared: PreparedStep = { ...driverStep };
  if (interactive) prepared.__interactive = interactive;
  if (skipIfMissing) prepared.__skipIfMissing = skipIfMissing;
  if (emptyState) prepared.__emptyState = emptyState;
  if (whenMissing) prepared.__whenMissing = whenMissing;
  if (advanceClick) prepared.__advanceClick = advanceClick;
  if (undoClick) prepared.__undoClick = undoClick;
  if (allowClick) prepared.__allowClick = allowClick;
  if (blockInteraction) {
    prepared.__blockInteraction = true;
    (prepared as DriveStep).disableActiveInteraction = true;
  }
  return prepared;
};

export const TourProvider: FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  // Read directly rather than through usePermission: the provider is rendered
  // on its own in tests, where a missing context means "no permissions".
  const permissionContext = useContext(PermissionContext);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentFlowId, setCurrentFlowId] = useState<string | null>(null);
  const currentFlowIdRef = useRef<string | null>(null);
  const stepIndexRef = useRef(0);
  const driverRef = useRef<Driver | null>(null);
  const listenerCleanupsRef = useRef<ListenerCleanup[]>([]);
  // Watches the currently-highlighted element for size changes so we can
  // re-measure the driver.js spotlight when async content (e.g. the award
  // letter / PDF) finishes rendering and grows the element after the step
  // appeared — otherwise the highlight stays at the element's initial
  // (loading-placeholder) size and clips the loaded content.
  const highlightResizeObserverRef = useRef<ResizeObserver | null>(null);
  // Set to true when WE destroy the driver (navigation to next page or
  // finishing the tour), so onDestroyStarted can tell the difference
  // between a user-initiated dismissal and our own cleanup.
  const programmaticDestroyRef = useRef(false);
  // Set to true while WE close a filter drawer on the tour's behalf (e.g. the
  // Back button stepping out of the drawer), so the close-button dismiss
  // listener doesn't mistake it for the user closing the drawer and end the
  // tour.
  const suppressDrawerDismissRef = useRef(false);
  // Bumped on every driveCurrentPage run. Because that function awaits the DOM
  // (resume walk-back, stepper restore, the first-element gate — up to seconds),
  // a fast route change can start a second run while the first is still
  // awaiting. Each run captures its generation and bails after an await if a
  // newer run has superseded it, so we never build a driver for a stale page.
  const driveGenerationRef = useRef(0);
  // When a `route-change` step is active we DON'T advance on click — a raw click
  // inside the highlighted table (opening a row's three-dot menu, a sort arrow,
  // pagination) would otherwise kill the tour even though nothing navigated.
  // Instead we arm this with the index to resume at, and the location-change
  // effect consumes it only once the URL genuinely changes (e.g. the menu's
  // "Send for Approval" navigates to the wizard). A click that navigates to the
  // wrong route still ends the tour gracefully (no matching steps there).
  const pendingRouteAdvanceRef = useRef<number | null>(null);
  // Route startTour navigated to, and when to stop waiting for it. React Router
  // v7 applies navigations inside `startTransition`, so the running flag
  // commits BEFORE the new pathname — a slow-rendering page left the location
  // effect driving the Help Center, finding no steps there and silently ending
  // the tour it had just started. Until the expected route commits (or the
  // deadline passes) the effect waits instead of driving the stale page.
  const expectedRouteRef = useRef<{ route: string; until: number } | null>(
    null
  );
  // Ends the tour the same way the popover's close button does (persist
  // progress, drop listeners, reset state). Set per page run; used by controls
  // outside the popover, e.g. a filter drawer's close / Cancel.
  const userDismissRef = useRef<(() => void) | null>(null);
  // Bumped by every startTour so the location effect re-drives even when
  // neither the pathname nor the running flag changes (restarting a tour that
  // lives on the current page, e.g. the Help Center tours).
  const [runId, setRunId] = useState(0);
  const latestPathRef = useRef('');
  const navigate = useNavigate();
  const location = useLocation();
  latestPathRef.current = location.pathname;

  // Drawers whose inline styles we've overridden, with their original values
  // so we can restore them when the tour ends.
  type RaisedDrawerRecord = {
    zIndex: string;
    pointerEvents: string;
    paper?: HTMLElement;
    paperZIndex?: string;
    paperPointerEvents?: string;
  };
  const raisedDrawersRef = useRef<Map<HTMLElement, RaisedDrawerRecord>>(
    new Map()
  );

  const raiseAncestorDrawer = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const drawer = el.closest('.MuiDrawer-root') as HTMLElement | null;
    if (!drawer) return;
    if (raisedDrawersRef.current.has(drawer)) return;

    // Save originals, then force drawer + paper above the overlay via
    // *inline* styles (beats SidePanel's hardcoded `sx={{ zIndex: 10 }}`).
    const paper = drawer.querySelector(
      '.MuiDrawer-paper'
    ) as HTMLElement | null;

    const record: RaisedDrawerRecord = {
      zIndex: drawer.style.zIndex,
      pointerEvents: drawer.style.pointerEvents,
    };
    drawer.style.zIndex = '1000000';
    drawer.style.pointerEvents = 'auto';

    if (paper) {
      record.paper = paper;
      record.paperZIndex = paper.style.zIndex;
      record.paperPointerEvents = paper.style.pointerEvents;
      paper.style.zIndex = '1000000';
      paper.style.pointerEvents = 'auto';
    }

    raisedDrawersRef.current.set(drawer, record);
  }, []);

  // Imperatively force any driver.js overlay/stage elements to pass clicks
  // through. Driver.js 1.x uses different class names across versions, so we
  // match loosely and set pointer-events: none inline (beats any CSS).
  const disableDriverOverlayClicks = useCallback(() => {
    const selectors = [
      '.driver-overlay',
      '#driver-page-overlay',
      '.driver-stage',
      'svg.driver-overlay',
      'svg[class*="driver-overlay"]',
      '[class*="driver-overlay"]',
      '[id*="driver-page-overlay"]',
    ];
    selectors.forEach(sel => {
      document.querySelectorAll<HTMLElement>(sel).forEach(node => {
        node.style.pointerEvents = 'none';
      });
    });
  }, []);

  // Inverse of disableDriverOverlayClicks: hand the overlay/stage back their
  // click-blocking so the highlighted element can't be clicked through. Used
  // on `blockInteraction` steps (terminal Submit / Send-for-Approval buttons)
  // where letting the real action fire would run a live submit and desync the
  // tour. Driver.js reuses the same overlay nodes across steps, so a prior
  // step's inline `pointer-events: none` would otherwise persist.
  const enableDriverOverlayClicks = useCallback(() => {
    const selectors = [
      '.driver-overlay',
      '#driver-page-overlay',
      '.driver-stage',
      'svg.driver-overlay',
      'svg[class*="driver-overlay"]',
      '[class*="driver-overlay"]',
      '[id*="driver-page-overlay"]',
    ];
    selectors.forEach(sel => {
      document.querySelectorAll<HTMLElement>(sel).forEach(node => {
        node.style.pointerEvents = '';
      });
    });
  }, []);

  // Is the element's top-left anchor actually on screen? driver.js decides
  // whether to scroll a step into view by testing the element's rect against the
  // WINDOW only. Our wizard pages scroll an inner container (`ContentBox`,
  // overflow:auto) that starts below the fixed header, so an element clipped
  // above the container's top can still sit at a positive window-Y — driver
  // thinks it's visible and skips scrolling, stranding the popover off-screen
  // (e.g. stepping Back from the footer Submit to the top stepper). We intersect
  // the window box with every scrollable ancestor's client box so a clipped
  // element is correctly seen as hidden. A tall element whose bottom overflows
  // is fine as long as its top is on screen.
  const isHighlightVisible = useCallback((el: HTMLElement): boolean => {
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    let top = 0;
    let left = 0;
    let bottom = window.innerHeight || document.documentElement.clientHeight;
    let right = window.innerWidth || document.documentElement.clientWidth;
    let node = el.parentElement;
    while (node && node !== document.body) {
      const style = globalThis.getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(style.overflowY + style.overflowX)) {
        const pr = node.getBoundingClientRect();
        top = Math.max(top, pr.top);
        left = Math.max(left, pr.left);
        bottom = Math.min(bottom, pr.bottom);
        right = Math.min(right, pr.right);
      }
      node = node.parentElement;
    }
    return (
      rect.top >= top &&
      rect.top < bottom &&
      rect.left >= left &&
      rect.left < right
    );
  }, []);

  // When a highlighted step lives inside a filter drawer, wire that drawer's
  // close (X) / Cancel controls so clicking them dismisses the TOUR too —
  // otherwise the drawer unmounts on close and the tour is left highlighting a
  // gone element. `destroy()` runs the non-programmatic dismissal path
  // (persists progress, restores state), returning the user to the list page.
  // Guarded with a dataset flag so consecutive drawer steps don't double-wire.
  const wireDrawerDismiss = useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const drawer = el.closest('.MuiDrawer-root') as HTMLElement | null;
    if (!drawer) return;
    const controls = drawer.querySelectorAll<HTMLElement>(
      '[aria-label="close"], .MuiButton-outlined'
    );
    controls.forEach(btn => {
      if (btn.dataset.tourDismissWired) return;
      btn.dataset.tourDismissWired = '1';
      const handler = () => {
        // The tour is closing the drawer itself (Back button) — let it close
        // but keep the tour running.
        if (suppressDrawerDismissRef.current) return;
        btn.removeEventListener('click', handler);
        delete btn.dataset.tourDismissWired;
        // driver.js's public destroy() skips onDestroyStarted, which would
        // leave the tour "running" with live listeners — dismiss properly.
        userDismissRef.current?.();
      };
      btn.addEventListener('click', handler);
      listenerCleanupsRef.current.push(() => {
        btn.removeEventListener('click', handler);
        delete btn.dataset.tourDismissWired;
      });
    });
  }, []);

  const restoreRaisedDrawers = useCallback(() => {
    raisedDrawersRef.current.forEach((record, drawer) => {
      drawer.style.zIndex = record.zIndex;
      drawer.style.pointerEvents = record.pointerEvents;
      if (record.paper) {
        record.paper.style.zIndex = record.paperZIndex ?? '';
        record.paper.style.pointerEvents = record.paperPointerEvents ?? '';
      }
    });
    raisedDrawersRef.current.clear();
  }, []);

  const destroyDriver = useCallback(() => {
    if (driverRef.current) {
      programmaticDestroyRef.current = true;
      destroyWithoutRefocus(driverRef.current);
      driverRef.current = null;
      programmaticDestroyRef.current = false;
    }
    restoreRaisedDrawers();
  }, [restoreRaisedDrawers]);

  const resetTourState = useCallback(() => {
    // Every path that ends a run lands here (Finish, close, stopTour), so this
    // is where the application goes back to live data. Clearing the demo state
    // too means the next tour starts from the same place this one did.
    setTourMode(false);
    resetTourDemoState();
    setIsTourRunning(false);
    setCurrentFlowId(null);
    currentFlowIdRef.current = null;
    stepIndexRef.current = 0;
    pendingRouteAdvanceRef.current = null;
    expectedRouteRef.current = null;
  }, []);

  const clearListeners = useCallback(() => {
    listenerCleanupsRef.current.forEach(fn => {
      try {
        fn();
      } catch {
        /* ignore */
      }
    });
    listenerCleanupsRef.current = [];
    setTourStepTargets([]);
    highlightResizeObserverRef.current?.disconnect();
    highlightResizeObserverRef.current = null;
  }, []);

  // The user's permissions decide which steps a run contains (steps can carry
  // their own gate). Held in a ref so the driver callbacks read the current
  // permissions without being rebuilt.
  const accessRef = useRef<TourAccess>({
    hasAnyPermission: () => false,
    permissionCodes: [],
  });
  accessRef.current = {
    hasAnyPermission: permissionContext?.hasAnyPermission ?? (() => false),
    permissionCodes: (permissionContext?.permissions ?? []).map(p => p.code),
  };

  // The step list this run follows. null until a flow with a `fallback` has
  // checked its data on its first page (see decideVariant).
  const variantRef = useRef<FlowVariant | null>(null);

  const stepsFor = useCallback(
    (flow: TourFlow, variant: FlowVariant | null): AppTourStep[] =>
      resolveFlowSteps(flow, accessRef.current, variant ?? 'main'),
    []
  );

  const getCurrentFlow = useCallback(
    (): TourFlow | undefined =>
      currentFlowIdRef.current
        ? getTourFlow(currentFlowIdRef.current)
        : undefined,
    []
  );

  const getFlowSteps = useCallback((): AppTourStep[] => {
    const flow = getCurrentFlow();
    return flow ? stepsFor(flow, variantRef.current) : [];
  }, [getCurrentFlow, stepsFor]);

  /**
   * Choose between a flow's main and fallback steps on its first page: wait
   * for the page's data to finish loading, then check for what the main steps
   * need. Runs before any popover shows, so the count is fixed from step 1.
   */
  const decideVariant = useCallback(async (flow: TourFlow): Promise<void> => {
    const fallback = flow.fallback;
    if (!fallback || variantRef.current) return;
    await waitForElement(
      fallback.readyWhen ?? fallback.requires,
      fallback.timeoutMs ?? 8000
    );
    variantRef.current = document.querySelector(fallback.requires)
      ? 'main'
      : 'fallback';
    localStorage.setItem(variantKeyFor(flow.id), variantRef.current);
  }, []);

  const markComplete = useCallback(() => {
    const flowId = currentFlowIdRef.current;
    if (flowId) {
      localStorage.setItem(storageKeyFor(flowId), 'true');
      localStorage.removeItem(progressKeyFor(flowId));
      localStorage.removeItem(variantKeyFor(flowId));
    }
    clearListeners();
    destroyDriver();
    resetTourState();
  }, [clearListeners, destroyDriver, resetTourState]);

  // Persist the current step so the Help Center can offer "Resume - Step X"
  // after a user dismisses the tour mid-flow.
  const persistProgress = useCallback(() => {
    const flowId = currentFlowIdRef.current;
    if (!flowId) return;
    const idx = stepIndexRef.current;
    if (idx > 0) {
      localStorage.setItem(progressKeyFor(flowId), String(idx));
    } else {
      localStorage.removeItem(progressKeyFor(flowId));
    }
  }, []);

  // End the run without completing it (the user left the tour's path):
  // keep their place for "Resume" and let them be.
  const stopDrive = useCallback(() => {
    persistProgress();
    clearListeners();
    resetTourState();
  }, [clearListeners, persistProgress, resetTourState]);

  /**
   * Settle a flow's main/fallback list before its first page is driven.
   * Returns false when the drive must stop: the user is not on the page the
   * data is checked on, or a newer drive superseded this one while waiting.
   */
  const settleVariant = useCallback(
    async (
      flow: TourFlow | undefined,
      currentPath: string,
      superseded: () => boolean
    ): Promise<boolean> => {
      if (!flow?.fallback || variantRef.current) return true;
      if (!routeMatches(flow.steps[0].route, currentPath)) {
        stopDrive();
        return false;
      }
      await decideVariant(flow);
      return !superseded();
    },
    [decideVariant, stopDrive]
  );

  /** Leave the current page's steps: finish the flow, or open the next page. */
  const leavePage = useCallback(
    (nextStepIndex: number, flowSteps: AppTourStep[]) => {
      stepIndexRef.current = nextStepIndex;
      destroyDriver();
      if (nextStepIndex >= flowSteps.length) markComplete();
      else void navigate(flowSteps[nextStepIndex].route);
    },
    [destroyDriver, markComplete, navigate]
  );

  const isFlowCompleted = useCallback(
    (flowId: string) => readCompleted(flowId),
    []
  );

  const getFlowProgress = useCallback(
    (flowId: string) => readProgress(flowId),
    []
  );

  // ── Helper: set up DOM event listeners for a single interactive step ──
  // Extracted to keep driveCurrentPage's Cognitive Complexity within budget.
  const setupInteractiveHandlers = useCallback(
    (
      target: HTMLElement,
      interactive: InteractiveStepConfig,
      isLastInPage: boolean,
      executeAdvance: (
        isLastInPage: boolean,
        interactive: InteractiveStepConfig
      ) => Promise<boolean>,
      listenerCleanups: (() => void)[],
      targetSelector?: string
    ) => {
      const advance = () => executeAdvance(isLastInPage, interactive);

      if (interactive.trigger === 'click') {
        // Delegate on the selector rather than binding to the captured node, so
        // the handler survives the target unmounting/remounting. E.g. the Final
        // Award "Proceed" button is removed while the user opens a file's
        // "Review" panel to clear exceptions, then a fresh button mounts on
        // return — a node-bound listener would stay on the detached element and
        // the new button would never advance the tour.
        // When the step spotlights a broad area but advances on a specific
        // control, `interactive.targetSelector` names that control (e.g. the
        // wizard stage content is highlighted, but only "Proceed" advances).
        const clickSelector = interactive.targetSelector || targetSelector;
        const matchesTarget = (ev: Event): boolean => {
          const node = ev.target;
          if (!(node instanceof Element)) return false;
          return clickSelector
            ? !!node.closest(clickSelector)
            : target.contains(node);
        };
        // Listen in the capture phase so we match BEFORE React handles the
        // click. React flushes click updates synchronously, so by the bubble
        // phase the target may already be re-rendered — e.g. a wizard's Next
        // becomes Submit on the last stage, and the node no longer matches the
        // Next selector, stranding the tour on this step.
        const handler = async (ev: Event) => {
          if (!matchesTarget(ev)) return;
          document.removeEventListener('click', handler, true);
          const advanced = await executeAdvance(isLastInPage, interactive);
          // Blocked gated action — re-arm so the user can fix the blocker and retry.
          if (!advanced && driverRef.current) {
            document.addEventListener('click', handler, true);
          }
        };
        document.addEventListener('click', handler, true);
        listenerCleanups.push(() =>
          document.removeEventListener('click', handler, true)
        );
        // 'route-change' steps have no click handler: a raw click inside the
        // highlighted region must not advance the tour. They advance via the
        // location-change effect once the URL actually changes (the pending
        // advance is armed in onHighlighted).
      } else if (interactive.trigger === 'input') {
        const { expectedValue } = interactive;
        // With an expected value the step waits for all of it, rather than
        // advancing on the first keystroke.
        const validate =
          interactive.validate ||
          ((node: HTMLElement) => {
            const value = (node as HTMLInputElement).value?.trim() ?? '';
            return expectedValue
              ? value.toLowerCase() === expectedValue.toLowerCase()
              : value.length > 0;
          });
        const inputEl = findStepInput(target);
        const handler = () => {
          if (validate(inputEl)) {
            inputEl.removeEventListener('input', handler);
            void advance();
          }
        };
        inputEl.addEventListener('input', handler);
        listenerCleanups.push(() =>
          inputEl.removeEventListener('input', handler)
        );
      } else if (interactive.trigger === 'appear' && interactive.waitFor) {
        // Nothing left to wait for — arming here would immediately re-advance
        // (see isAppearAlreadySatisfied). The popover offers Next instead, so
        // the step stays readable rather than flicking forward.
        if (isAppearAlreadySatisfied(interactive)) return;
        const selector = interactive.waitFor;
        const isReady = () => {
          const node = document.querySelector(selector);
          if (!node) return false;
          const rect = node.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        };
        const observer = new MutationObserver(() => {
          if (!isReady()) return;
          observer.disconnect();
          void advance();
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
        listenerCleanups.push(() => observer.disconnect());
      } else if (interactive.trigger === 'select') {
        const optionSelector =
          interactive.optionSelector || '.MuiAutocomplete-option';
        const handler = (event: Event) => {
          const clicked = event.target as Element | null;
          if (clicked instanceof Element && clicked.closest(optionSelector)) {
            document.removeEventListener('mousedown', handler);
            setTimeout(advance, 150);
          }
        };
        document.addEventListener('mousedown', handler);
        listenerCleanups.push(() =>
          document.removeEventListener('mousedown', handler)
        );
      }
    },
    [driverRef]
  );

  // ── Helper: resume walk-back for non-stepper flows ──
  // Rewinds startIdx to the nearest earlier same-route step whose anchor is
  // present in the DOM, so transient-state steps re-run and rebuild state.
  const resumeWalkBack = useCallback(
    async (
      startIdxIn: number,
      flowSteps: AppTourStep[],
      currentPath: string,
      superseded: () => boolean
    ): Promise<number> => {
      let runStart = startIdxIn;
      while (
        runStart > 0 &&
        routeMatches(flowSteps[runStart - 1].route, currentPath)
      ) {
        runStart -= 1;
      }
      const entrySel = flowSteps[runStart].element;
      if (typeof entrySel === 'string') {
        await waitForElement(entrySel, 6000);
      }
      await new Promise(r => setTimeout(r, 120));
      if (superseded()) return startIdxIn;
      let startIdx = startIdxIn;
      while (startIdx > runStart) {
        const sel = flowSteps[startIdx].element;
        if (typeof sel === 'string' && document.querySelector(sel)) break;
        startIdx -= 1;
      }
      return startIdx;
    },
    []
  );

  // ── Helper: collect consecutive steps on the current route ──
  const collectPageSteps = useCallback(
    (
      flowSteps: AppTourStep[],
      startIdx: number,
      currentPath: string
    ): PreparedStep[] => {
      const pageSteps: PreparedStep[] = [];
      let i = startIdx;
      while (
        i < flowSteps.length &&
        routeMatches(flowSteps[i].route, currentPath)
      ) {
        pageSteps.push(prepareStep(flowSteps[i]));
        i++;
      }
      return pageSteps;
    },
    []
  );

  // Drive the current page's steps starting from stepIndexRef
  const driveCurrentPage = useCallback(
    async (currentPath: string) => {
      clearListeners();
      destroyDriver();
      const myGen = ++driveGenerationRef.current;
      const superseded = () => driveGenerationRef.current !== myGen;

      const flowMeta = getCurrentFlow();
      // A flow with backup steps settles which list it runs on its first page,
      // before anything is shown.
      if (!(await settleVariant(flowMeta, currentPath, superseded))) return;
      const flowSteps = getFlowSteps();
      let startIdx = stepIndexRef.current;
      if (startIdx >= flowSteps.length) {
        markComplete();
        return;
      }

      // ── Resume walk-back (non-stepper flows) ──
      // A resumed step may anchor to an element that only exists after a
      // transient interaction this fresh load hasn't replayed (an open filter
      // drawer, an active non-default tab, a loaded search result, a selected
      // dashboard card). If the saved step's anchor isn't in the DOM, step back
      // to the nearest earlier same-route step whose anchor IS present and
      // resume there, so the interactive steps re-run and rebuild that state. A
      // saved step whose anchor already exists (e.g. a toolbar filter button)
      // resumes exactly where it left off — no needless restart. Stepper
      // wizards are handled separately (they restore their stage, below).
      if (startIdx > 0 && !flowMeta?.stepperWizard) {
        startIdx = await resumeWalkBack(
          startIdx,
          flowSteps,
          currentPath,
          superseded
        );
        if (superseded()) return;
        stepIndexRef.current = startIdx;
      }

      // Collect consecutive steps on the current route
      const pageSteps = collectPageSteps(flowSteps, startIdx, currentPath);

      if (pageSteps.length === 0) {
        // User navigated away from the tour path (e.g., back to Help Center
        // via the sidebar). Don't force-navigate them back — just stop.
        stopDrive();
        return;
      }

      // ── Resume into a single-route stepper wizard ──
      // These flows (Create Award, User Onboarding) remount on stage 1, so a
      // resumed deep step would highlight a late popover over the first stage.
      // Drive the wizard to the saved step's stage first by clicking the
      // matching stage label.
      const resumeStepperWizard = async (): Promise<boolean> => {
        if (!flowMeta?.stepperWizard || startIdx <= 0) return false;
        const stage = flowSteps
          .slice(0, startIdx)
          .filter(
            s => s.interactive?.trigger === 'click' || !!s.advanceClick
          ).length;
        if (stage > 0) {
          await waitForElement(STEPPER_ROOT_SELECTOR, 6000);
          const labels = document.querySelectorAll<HTMLElement>(
            STEPPER_LABEL_SELECTOR
          );
          labels[stage]?.click();
          // Let the stepper swap in the target stage's content before we gate.
          await new Promise(r => setTimeout(r, 200));
          if (superseded()) return true;
        }
        return false;
      };

      if (await resumeStepperWizard()) return;

      // ── Gate: wait for the page's first step to be present and sized ──
      const firstEl = await waitForPageEntry(pageSteps);
      // Tiny settle delay so any post-load layout shifts finish.
      await new Promise(r => setTimeout(r, 120));
      if (superseded()) return;

      // Pre-raise any ancestor drawer BEFORE driver.js renders its overlay,
      // so the drawer is already positioned above the overlay from the very
      // first frame. Otherwise there's a flicker where the drawer appears
      // dimmed and click-blocked until `onHighlighted` fires.
      if (firstEl) {
        raiseAncestorDrawer(firstEl);
      }

      const nextStepIndex = startIdx + pageSteps.length;
      const isLastPage = nextStepIndex >= flowSteps.length;
      const lastPageStepIdx = pageSteps.length - 1;

      // Starting at `from`, return the first step index whose `skipIfMissing`
      // target is currently in the DOM (or has no `skipIfMissing`). Steps that
      // point at conditionally-rendered elements (e.g. Accept/Reject buttons
      // that only show for certain award statuses) are skipped when absent.
      const nextVisibleIdx = (from: number): number => {
        let idx = from;
        while (idx <= lastPageStepIdx) {
          const sel = pageSteps[idx].__skipIfMissing;
          if (!sel || document.querySelector(sel)) break;
          idx++;
        }
        return idx;
      };

      /** The same walk backwards, so Back skips whatever Next skipped. */
      const prevVisibleIdx = (from: number): number => {
        let idx = from;
        while (idx >= 0) {
          const sel = pageSteps[idx].__skipIfMissing;
          if (!sel || document.querySelector(sel)) break;
          idx--;
        }
        return idx;
      };

      // Helper to execute the advance logic. Returns false when a
      // `requireWaitFor` step was BLOCKED (its `waitFor` never appeared) — the
      // caller then re-arms so the user can resolve the blocker and retry,
      // instead of the popover advancing while the screen didn't change.
      const executeAdvance = async (
        isLastInPage: boolean,
        interactive: InteractiveStepConfig
      ): Promise<boolean> => {
        let appeared = true;
        if (interactive.waitFor) {
          const el = await waitForElement(
            interactive.waitFor,
            interactive.waitForTimeoutMs ?? 3000
          );
          appeared = !!el;
          // A drawer is still sliding in when it first matches; place the next
          // popover only once it has come to rest.
          await waitForLayoutSettle(el?.closest('.MuiDrawer-paper') ?? el);
        }
        // The tour ended or restarted while we waited — this run is stale.
        if (superseded() || !driverRef.current) return true;
        // Gated action (e.g. a wizard Proceed refusing until validation passes):
        // don't advance the tour if the expected next-stage signal never showed.
        if (interactive.requireWaitFor && !appeared) return false;
        if (isLastInPage) {
          leavePage(nextStepIndex, flowSteps);
        } else {
          driverRef.current.moveNext();
        }
        return true;
      };

      // Listeners each step armed on its last highlight. Back and Next can
      // highlight a step again without leaving the page, so its earlier ones
      // are dropped first instead of stacking up.
      const stepListenerCleanups = new Map<number, ListenerCleanup[]>();

      // Wire interactive steps
      pageSteps.forEach((step, idx) => {
        const interactive = step.__interactive;
        if (!interactive) return;

        // The final step of a flow must offer a Finish button even when it's
        // interactive (the filter tours end on "Apply Filters"), so the user
        // can complete the tour — and have it marked completed — without being
        // forced to perform the live action. Mid-flow interactive steps keep
        // only Back + close so the user must do the action to advance.
        const isLastStepOfFlow = isLastPage && idx === lastPageStepIdx;
        // The first step shown on a page has no in-page step to go Back to (each
        // page drives only its own steps; Back can't cross a route boundary), so
        // drop the previous button there.
        const isFirstStepOfPage = idx === 0;
        const baseButtons: AllowedButtons[] =
          isLastStepOfFlow || interactive.showNext
            ? ['previous', 'next', 'close']
            : ['previous', 'close'];
        const interactiveButtons = baseButtons.filter(
          btn => !(isFirstStepOfPage && btn === 'previous')
        );
        step.popover = {
          ...step.popover,
          showButtons: interactiveButtons,
          popoverClass: [
            (step.popover && (step.popover as any).popoverClass) || '',
            'driver-interactive',
          ]
            .filter(Boolean)
            .join(' '),
        };

        step.onHighlightStarted = el => {
          if (!el) return;
          const target = el as HTMLElement;
          stepListenerCleanups.get(idx)?.forEach(fn => fn());
          const stepCleanups: ListenerCleanup[] = [];
          stepListenerCleanups.set(idx, stepCleanups);
          raiseAncestorDrawer(target);
          disableDriverOverlayClicks();
          // A step the user can leave with Next keeps its listener armed after
          // they do, so the action performed later (e.g. typing in the search
          // box on the following step) must not advance from wherever the tour
          // now is.
          const advanceFromThisStep: typeof executeAdvance =
            interactive.showNext
              ? async (isLast, config) =>
                  driverRef.current?.getActiveIndex() === idx
                    ? executeAdvance(isLast, config)
                    : true
              : executeAdvance;
          if (interactive.expectedValue) {
            stepCleanups.push(
              restrictInputToValue(
                target,
                interactive.expectedValue,
                // Only while this step is showing: once the tour moves on, the
                // box is the user's again.
                () => driverRef.current?.getActiveIndex() === idx
              )
            );
          }
          setupInteractiveHandlers(
            target,
            interactive,
            idx === lastPageStepIdx,
            advanceFromThisStep,
            stepCleanups,
            typeof step.element === 'string' ? step.element : undefined
          );
          // clearListeners still drops them when the page or tour ends.
          listenerCleanupsRef.current.push(...stepCleanups);
        };

        (step as any).disableActiveInteraction = false;
      });

      // The first step shown on a page has nowhere to go Back to. Interactive
      // steps already handle this above; for a non-interactive first step,
      // override the global default ('next'/'previous'/'close') to drop Back.
      if (pageSteps[0] && !pageSteps[0].__interactive) {
        pageSteps[0].popover = {
          ...pageSteps[0].popover,
          showButtons: ['next', 'close'],
        };
      }

      const handlePrevClick = (activeIdx: number) => {
        const curSel = pageSteps[activeIdx]?.element;
        const curEl =
          typeof curSel === 'string' ? document.querySelector(curSel) : null;
        const drawer = curEl?.closest('.MuiDrawer-root') as HTMLElement | null;
        if (drawer && activeIdx > 0) {
          const prevSel = pageSteps[activeIdx - 1]?.element;
          const prevEl =
            typeof prevSel === 'string'
              ? document.querySelector(prevSel)
              : null;
          // Only close if the previous step isn't itself inside this drawer.
          if (!prevEl || !drawer.contains(prevEl)) {
            suppressDrawerDismissRef.current = true;
            (
              drawer.querySelector('[aria-label="close"]') as HTMLElement | null
            )?.click();
            suppressDrawerDismissRef.current = false;
          }
        }
        // Stepper wizards: walk the wizard's stepper BACK to the stage the
        // previous step belongs to (symmetric to the resume stage-restore).
        if (flowMeta?.stepperWizard && activeIdx > 0) {
          const stage = flowSteps
            .slice(0, startIdx + activeIdx - 1)
            .filter(
              s => s.interactive?.trigger === 'click' || !!s.advanceClick
            ).length;
          const labels = document.querySelectorAll<HTMLElement>(
            STEPPER_LABEL_SELECTOR
          );
          labels[stage]?.click();
        }
      };

      const onPrevClickFn = () => {
        const obj = driverRef.current;
        if (!obj) return;
        const activeIdx = obj.getActiveIndex() ?? 0;
        // Walk back past steps whose target isn't on screen — otherwise Back
        // lands on the step Next just skipped (a popover with nothing to
        // point at).
        const target = prevVisibleIdx(activeIdx - 1);

        // Nothing left to go back to on this page. If the flow began on an
        // earlier page, cross back into it the way Next crosses forward —
        // without this, Back is a dead button on the first step of every page
        // after the first, which is most of the multi-page flows.
        if (target < 0) {
          const previousStepIdx = startIdx - 1;
          // Already on the flow's very first step.
          if (previousStepIdx < 0) return;
          // A detail route carries a record id we cannot rebuild from the step
          // definition, so there is no URL to navigate back to. Flows only ever
          // run list → detail, so this does not arise in practice.
          if (isDynamicRoute(flowSteps[previousStepIdx].route)) return;
          // Skip handlePrevClick: its drawer close and stepper walk-back act on
          // the page we are leaving. On arrival, driveCurrentPage's resume
          // walk-back settles on the nearest earlier step that is actually on
          // screen.
          leavePage(previousStepIdx, flowSteps);
          return;
        }

        handlePrevClick(activeIdx);
        const moveBack = () => {
          if (target === activeIdx - 1) obj.movePrevious();
          else obj.moveTo(target);
        };

        // The step being returned to moved the page on (e.g. its Next opened
        // the wizard's second stage and unmounted the button it points at).
        // Undo that first, and highlight it only once its target is back.
        const undoSel = pageSteps[target].__undoClick;
        if (!undoSel) {
          moveBack();
          return;
        }
        document.querySelector<HTMLElement>(undoSel)?.click();
        const targetSel = pageSteps[target].element;
        void (
          typeof targetSel === 'string'
            ? waitForElement(targetSel, 3000)
            : Promise.resolve(null)
        ).then(() => {
          if (superseded() || driverRef.current !== obj) return;
          moveBack();
        });
      };

      const onPopoverRenderFn = (popover: any) => {
        const obj = driverRef.current;
        if (!obj) return;
        const activeIdx = obj.getActiveIndex() ?? 0;
        if (popover.progress) {
          // The count comes from the resolved step list (role, permissions
          // and data variant were settled before the run), never from the
          // live DOM — so it cannot change as the tour moves between pages.
          popover.progress.innerText = `Step ${startIdx + activeIdx + 1} of ${
            flowSteps.length
          }`;
        }
        // Swap to empty-state copy when the step's target is showing an empty
        // state (e.g. a data table with no rows), so we don't tell the user to
        // interact with rows that aren't there. See
        // [[tours-no-empty-table-dependency]].
        // An empty table is the more specific state (nothing is missing from
        // rows that do not exist), so its copy is applied last and wins.
        applyMissingStateCopy(popover, pageSteps[activeIdx]?.__whenMissing);
        applyEmptyStateCopy(popover, pageSteps[activeIdx]?.__emptyState);
        // A left-side popover anchored inside a side drawer otherwise butts up
        // against the drawer's edge — nudge it away (see tour.css).
        const activeStep = pageSteps[activeIdx];
        const stepEl =
          typeof activeStep?.element === 'string'
            ? document.querySelector(activeStep.element)
            : null;
        popover.wrapper?.classList.toggle(
          'driver-popover-beside-drawer',
          activeStep?.popover?.side === 'left' &&
            !!stepEl?.closest('.MuiDrawer-paper')
        );
        // An `appear` step whose target is already on screen has no listener
        // armed, and mid-flow interactive steps carry only Back + close — so
        // without this the user lands here (typically via Back) with no way
        // forward. driver.js hides the button with `display: none`.
        if (
          isAppearAlreadySatisfied(pageSteps[activeIdx]?.__interactive) &&
          popover.nextButton
        ) {
          popover.nextButton.style.display = 'block';
        }
        // Later steps on this page can be "skipped" purely because the user
        // has not performed this step's action yet — the Simulate Policy result
        // panel does not exist until the simulation is run. Labelling the
        // button Finish there tells the user the tour is over while it is still
        // asking them to do something, so keep Next until the action lands. A
        // genuinely final step (nothing after it) still reads Finish.
        const activeWaitFor =
          pageSteps[activeIdx]?.__interactive?.waitFor ?? null;
        const awaitingActionThatRevealsSteps =
          activeIdx < lastPageStepIdx &&
          !!activeWaitFor &&
          !document.querySelector(activeWaitFor);

        if (
          isLastPage &&
          !awaitingActionThatRevealsSteps &&
          nextVisibleIdx(activeIdx + 1) > lastPageStepIdx &&
          popover.nextButton
        ) {
          popover.nextButton.innerText = 'Finish';
        }
      };

      const onHighlightedFn = (el: any) => {
        const obj = driverRef.current;
        if (!obj) return;
        raiseAncestorDrawer(el as HTMLElement | null);
        wireDrawerDismiss(el as HTMLElement | null);
        const activeIdx = obj.getActiveIndex() ?? 0;
        // The controls this step points at stay clickable for the user; the
        // views a tour steers ignore their other clicks (isBlockedByTour).
        const activeStep = pageSteps[activeIdx];
        setTourStepTargets([
          typeof activeStep?.element === 'string'
            ? activeStep.element
            : undefined,
          activeStep?.__advanceClick,
          activeStep?.__interactive?.targetSelector,
          activeStep?.__allowClick,
        ]);
        // Arm (or disarm) the pending route advance: while a route-change step
        // is the active one, the tour advances only when the URL changes — the
        // location effect consumes this. Any other active step disarms it.
        pendingRouteAdvanceRef.current =
          pageSteps[activeIdx]?.__interactive?.trigger === 'route-change'
            ? nextStepIndex
            : null;
        const applyOverlayClicks = () => {
          if (pageSteps[activeIdx]?.__blockInteraction) {
            enableDriverOverlayClicks();
          } else {
            disableDriverOverlayClicks();
          }
        };
        applyOverlayClicks();

        // Scroll the step into view when driver skipped it (its window-only
        // check misses elements clipped by an inner scroll container — see
        // isHighlightVisible). refresh() then repositions the spotlight/popover
        // onto the now-visible element, and re-renders the overlay, so re-apply
        // our click-through state afterwards.
        const stepEl = el as HTMLElement | null;
        if (stepEl && !isHighlightVisible(stepEl)) {
          stepEl.scrollIntoView({
            block:
              stepEl.offsetHeight > window.innerHeight ? 'start' : 'center',
            inline: 'center',
          });
          obj.refresh();
          applyOverlayClicks();
        }

        // Re-measure the spotlight if the highlighted element grows after the
        // step appears — e.g. the award letter / PDF renders a beat later and
        // the element jumps from a loading placeholder to its full height. Skip
        // the observer's initial synchronous fire; only a genuine later resize
        // refreshes. refresh() re-renders the overlay, so re-apply our overlay
        // click-through state afterwards.
        highlightResizeObserverRef.current?.disconnect();
        highlightResizeObserverRef.current = null;
        const highlightedEl = el as HTMLElement | null;
        if (highlightedEl && typeof ResizeObserver !== 'undefined') {
          let primed = false;
          const observer = new ResizeObserver(() => {
            if (!primed) {
              primed = true;
              return;
            }
            driverRef.current?.refresh();
            applyOverlayClicks();
          });
          observer.observe(highlightedEl);
          highlightResizeObserverRef.current = observer;
        }
      };

      const onNextClickFn = () => {
        const obj = driverRef.current;
        if (!obj) return;
        const activeIdx = obj.getActiveIndex() ?? 0;
        // Drive the page forward via the popover's own Next: click the wizard's
        // Next/Proceed button so the page stage advances in lockstep with the
        // tour (the user shouldn't have to hunt for the dimmed page button).
        const advanceSel = pageSteps[activeIdx]?.__advanceClick;
        const target = nextVisibleIdx(activeIdx + 1);

        // Capture the next step's current target BEFORE the click: an
        // advanceClick that swaps a keyed subtree (e.g. the dashboard section,
        // keyed by card code) detaches this node and mounts a fresh one on a
        // later tick. We advance now (correct when nothing remounts) and, if the
        // node does get replaced, re-anchor onto the live one — see
        // reanchorOnRemount.
        const nextSel = pageSteps[target]?.element;
        const staleNext =
          advanceSel && typeof nextSel === 'string' && target <= lastPageStepIdx
            ? document.querySelector(nextSel)
            : null;

        if (advanceSel) {
          document.querySelector<HTMLElement>(advanceSel)?.click();
        }

        if (target > lastPageStepIdx) {
          leavePage(nextStepIndex, flowSteps);
          return;
        }
        if (target === activeIdx + 1) {
          obj.moveNext();
        } else {
          obj.moveTo(target);
        }

        if (staleNext && typeof nextSel === 'string') {
          listenerCleanupsRef.current.push(
            reanchorOnRemount(driverRef, nextSel, staleNext, target)
          );
        }
      };

      const onDestroyStartedFn = () => {
        const obj = driverRef.current;
        if (!obj) return;
        if (programmaticDestroyRef.current) return;
        const activeIdx = obj.getActiveIndex() ?? 0;
        stepIndexRef.current = startIdx + activeIdx;
        persistProgress();
        clearListeners();
        resetTourState();
        programmaticDestroyRef.current = true;
        driverRef.current = null;
        destroyWithoutRefocus(obj);
        programmaticDestroyRef.current = false;
      };

      const driverObj = driver({
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        allowClose: false,
        overlayColor: '#000000',
        overlayOpacity: 0.45,
        stagePadding: 6,
        stageRadius: 6,
        steps: pageSteps,
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: isLastPage ? 'Finish' : 'Next Page →',
        progressText: 'Step {{current}} of {{total}}',
        onPopoverRender: onPopoverRenderFn,
        onHighlighted: onHighlightedFn,
        onNextClick: onNextClickFn,
        onPrevClick: onPrevClickFn,
        onDestroyStarted: onDestroyStartedFn,
      });

      driverRef.current = driverObj;
      userDismissRef.current = onDestroyStartedFn;
      // The page's first step can be skippable too — start on the first one
      // whose target is present, or move straight on if none of them are.
      const firstVisible = nextVisibleIdx(0);
      if (firstVisible > lastPageStepIdx) {
        leavePage(nextStepIndex, flowSteps);
        return;
      }
      driverObj.drive(firstVisible);
    },
    [
      clearListeners,
      collectPageSteps,
      destroyDriver,
      disableDriverOverlayClicks,
      enableDriverOverlayClicks,
      wireDrawerDismiss,
      getCurrentFlow,
      getFlowSteps,
      isHighlightVisible,
      leavePage,
      markComplete,
      persistProgress,
      raiseAncestorDrawer,
      resetTourState,
      resumeWalkBack,
      settleVariant,
      setupInteractiveHandlers,
      stopDrive,
    ]
  );

  // Drive the page whenever the route (or running state) changes while a
  // tour is active. This covers both the initial launch from the Help Center
  // (Help Center → first flow route) and cross-page transitions mid-tour.
  useEffect(() => {
    if (!isTourRunning) return undefined;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const timer = setTimeout(() => {
      const expected = expectedRouteRef.current;
      if (expected && !routeMatches(expected.route, location.pathname)) {
        // Still navigating to the tour's start page — the pathname commit
        // re-runs this effect. If it never lands (e.g. a guard redirected
        // elsewhere), stop waiting and let the drive end the tour.
        fallback = setTimeout(
          () => {
            if (expectedRouteRef.current !== expected) return;
            expectedRouteRef.current = null;
            void driveCurrentPage(latestPathRef.current);
          },
          Math.max(0, expected.until - Date.now())
        );
        return;
      }
      expectedRouteRef.current = null;
      // A route-change step armed a pending advance; the URL has now changed, so
      // resume at the next step. If the new route doesn't match (e.g. the user
      // opened a read-only row preview instead of the wizard), driveCurrentPage
      // finds no steps and ends the tour — as the step's copy warns.
      if (pendingRouteAdvanceRef.current != null) {
        stepIndexRef.current = pendingRouteAdvanceRef.current;
        pendingRouteAdvanceRef.current = null;
      }
      void driveCurrentPage(location.pathname);
    }, 100);
    return () => {
      clearTimeout(timer);
      clearTimeout(fallback);
    };
  }, [location.pathname, isTourRunning, runId, driveCurrentPage]);

  // Toggle a body class while a tour is running so CSS can bump MUI portal
  // z-indexes (Autocomplete / Select / Menu dropdowns) above the driver.js
  // overlay. Without this, clicking a highlighted dropdown opens the menu
  // *behind* the dark overlay and looks frozen.
  useEffect(() => {
    if (isTourRunning) {
      document.body.classList.add('tour-running');
    } else {
      document.body.classList.remove('tour-running');
    }
    return () => {
      document.body.classList.remove('tour-running');
    };
  }, [isTourRunning]);

  // Toggle a separate class only for flows that opt into suppressing plain
  // row-click navigation on their list page (so the user follows the row's
  // three-dot menu instead of dead-ending on a read-only preview). Flows
  // without the flag — e.g. a preview-focused tour — keep normal row clicks.
  useEffect(() => {
    const suppress =
      isTourRunning &&
      !!(currentFlowId && getTourFlow(currentFlowId)?.suppressRowNav);
    if (suppress) {
      document.body.classList.add('tour-suppress-row-nav');
    } else {
      document.body.classList.remove('tour-suppress-row-nav');
    }
    return () => {
      document.body.classList.remove('tour-suppress-row-nav');
    };
  }, [isTourRunning, currentFlowId]);

  const startTour = useCallback(
    (flowId: string) => {
      const flow = getTourFlow(flowId);
      if (!flow) return;

      // Pick up saved progress if a tour was previously interrupted. A
      // completed flow resumes from the top (the Help Center clears it via
      // resetFlow before getting here in the "Start Again" case). The saved
      // index belongs to the step list the run was on, so a flow with backup
      // steps resumes on that list — and without a record of it, restarts.
      const savedIdx = readProgress(flowId);
      const savedVariant = flow.fallback ? readVariant(flowId) : 'main';
      const variant = savedIdx > 0 ? savedVariant : null;
      const steps = stepsFor(flow, variant);
      if (steps.length === 0) return;

      // Tear down any tour already on screen and invalidate its in-flight
      // async work, so the new run starts clean.
      clearListeners();
      destroyDriver();
      driveGenerationRef.current += 1;
      pendingRouteAdvanceRef.current = null;
      expectedRouteRef.current = null;

      const resumeIdx = resolveResumeIdx(steps, savedIdx, variant);
      // Back at the top, a flow with backup steps re-checks its data.
      variantRef.current = resumeIdx > 0 ? variant : null;
      if (!variantRef.current) localStorage.removeItem(variantKeyFor(flowId));
      // NOTE: we resume at the saved step as-is. If that step's anchor isn't
      // present on a fresh load (it depended on transient state like an open
      // drawer), driveCurrentPage's resume walk-back rewinds to the nearest
      // earlier step whose anchor IS present — so safe steps resume exactly
      // where they left off and only state-dependent steps step back.

      // Switch to sample data before the navigation below: the page that
      // mounts on arrival fires its fetches immediately, and they have to be
      // answered from the fixtures rather than the tenant's own records. This
      // is imperative rather than React state for the same reason — the flag
      // has to be readable from the axios layer on the very next tick.
      resetTourDemoState();
      setTourMode(true, flowId);

      currentFlowIdRef.current = flowId;
      setCurrentFlowId(flowId);
      stepIndexRef.current = resumeIdx;
      localStorage.removeItem(storageKeyFor(flowId));

      const targetRoute = steps[resumeIdx].route;
      const alreadyThere = globalThis.window.location.pathname === targetRoute;
      // A page that keeps its view in history state (the Administration Hub's
      // card and module tab) is opened on the view the flow is about, even
      // when the user is already on it with another card showing.
      const startState =
        targetRoute === flow.startRoute ? flow.startState : undefined;
      if (!isDynamicRoute(targetRoute) && (!alreadyThere || startState)) {
        if (!alreadyThere) {
          expectedRouteRef.current = {
            route: targetRoute,
            until: Date.now() + ROUTE_ARRIVAL_TIMEOUT_MS,
          };
        }
        void navigate(
          targetRoute,
          startState ? { state: startState, replace: alreadyThere } : undefined
        );
      }
      // Flip running flag LAST so the route-change effect picks it up and
      // drives the page — whether we just navigated or were already there.
      setIsTourRunning(true);
      setRunId(id => id + 1);
    },
    [clearListeners, destroyDriver, navigate, stepsFor]
  );

  const stopTour = useCallback(() => {
    clearListeners();
    destroyDriver();
    resetTourState();
  }, [clearListeners, destroyDriver, resetTourState]);

  const resetFlow = useCallback((flowId: string) => {
    localStorage.removeItem(storageKeyFor(flowId));
    localStorage.removeItem(progressKeyFor(flowId));
    localStorage.removeItem(variantKeyFor(flowId));
  }, []);

  const resetAllFlows = useCallback(() => {
    tourFlows.forEach(f => {
      localStorage.removeItem(storageKeyFor(f.id));
      localStorage.removeItem(progressKeyFor(f.id));
      localStorage.removeItem(variantKeyFor(f.id));
    });
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  }, []);

  const value = useMemo(
    () => ({
      isTourRunning,
      currentFlowId,
      isFlowCompleted,
      getFlowProgress,
      startTour,
      stopTour,
      resetFlow,
      resetAllFlows,
    }),
    [
      isTourRunning,
      currentFlowId,
      isFlowCompleted,
      getFlowProgress,
      startTour,
      stopTour,
      resetFlow,
      resetAllFlows,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
